import { useState, type FormEvent } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Shown in place of a hard redirect when the session dies while the user is mid-task (e.g. mid
 * chat). The page underneath stays mounted — nothing is unmounted, no navigation happens — so any
 * in-progress streaming response or unsynced local draft keeps ticking along in the background and
 * picks back up the moment login succeeds (see src/lib/sessionExpiry.ts's session-restored event).
 */
export function SessionExpiredDialog() {
  const { sessionExpired, login, logout } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(username, password);
      setPassword("");
      // On success AuthContext clears `sessionExpired` itself, which closes this dialog.
    } catch {
      // AuthContext already surfaces a toast on failure; just let the user retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={sessionExpired}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Your session has expired</AlertDialogTitle>
          <AlertDialogDescription>
            Sign in again to continue. Nothing here is lost — your conversation is saved on this
            device and will finish syncing automatically as soon as you're signed back in.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="session-expired-username">Username or email</Label>
            <Input
              id="session-expired-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              data-testid="input-session-expired-username"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="session-expired-password">Password</Label>
            <Input
              id="session-expired-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="input-session-expired-password"
            />
          </div>
          <AlertDialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => void logout()}
              data-testid="button-session-expired-logout"
            >
              Log out instead
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="button-session-expired-submit">
              {isSubmitting ? "Signing in..." : "Sign in"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
